/**
 * Moneyness-based volatility skew implementation
 * 
 * Implements a parabolic volatility smile that adjusts implied volatility
 * based on how far the strike is from the current spot price (moneyness).
 * 
 * This creates a "sticky delta" model where volatility follows the spot price,
 * critical for crypto markets where prices can move 10-20% in a day.
 */

/**
 * Calculate implied volatility with moneyness-based skew
 * 
 * Uses a parabolic function of log-moneyness to create a volatility smile:
 * - ATM options use base volatility
 * - OTM options (both calls and puts) have higher volatility
 * - Creates a "U-shaped smile" typical in crypto markets
 * 
 * @param S - Spot price
 * @param K - Strike price
 * @param baseVol - ATM volatility (e.g., 0.80 for 80%)
 * @param skewFactor - Adjusts steepness of the smile (default: 10.0). Higher = more expensive wings
 * @returns Adjusted implied volatility
 */
export function getImpliedVolatility(
  S: number,
  K: number,
  baseVol: number,
  skewFactor: number = 10.0,
): number {
  if (S <= 0 || K <= 0 || baseVol <= 0) {
    return baseVol;
  }

  // Calculate Log-Moneyness
  // Log-moneyness allows us to treat 10% up and 10% down roughly symmetrically
  // x = 0 at the money. x > 0 for OTM calls, x < 0 for OTM puts
  const x = Math.log(K / S);

  // Apply Parabolic Skew (The "Smile")
  // We square 'x' so that volatility increases whether we go up or down
  // volatility = base + (skew * x^2)
  const volatility = baseVol + skewFactor * (x * x);

  // Ensure volatility stays positive
  return Math.max(volatility, 0.01);
}

